import { Box, CircularProgress } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { fadeOutAnimation } from "./AnimatedAvatar/styles";
import { useIsDesktop } from "@utils/hooks/useIsDesktop";

type LoadingProps = React.PropsWithChildren<{
  show?: boolean;
  onProgress?: (progress: number) => void;
  onLoaded?: () => void;
  durationInMs?: number;
  chunkCount?: number;
  closeDelayInMs?: number;
}>;

const handleProgress = async ({
  chunkCount,
  durationInMs,
  onProgress,
}: Required<
  Pick<
    LoadingProps,
    "chunkCount" | "durationInMs" | "onProgress" | "closeDelayInMs"
  >
>) => {
  const interval = durationInMs / chunkCount;
  for (let i = 0; i < chunkCount; i++) {
    await new Promise((resolve) => setTimeout(resolve, interval));
    onProgress(((i + 1) / chunkCount) * 100);
  }
};

const Loading = ({
  show = false,
  onProgress,
  onLoaded,
  children,
  durationInMs = 2000,
  chunkCount = 10,
  closeDelayInMs = 200,
}: LoadingProps) => {
  const isDesktop = useIsDesktop();
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleLoading = async () => {
      await handleProgress({
        chunkCount,
        durationInMs,
        closeDelayInMs,
        onProgress: (progress) => {
          setProgress(progress);
          onProgress?.(progress);
        },
      });
      onLoaded?.();
    };

    if (!show) {
      return;
    }
    handleLoading();
  }, [show, chunkCount, durationInMs, closeDelayInMs, onProgress]);

  const animationDuration = useMemo(
    () => closeDelayInMs / 1000,
    [closeDelayInMs]
  );

  const loadingSize = isDesktop ? "30rem" : "15rem";

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        transition: `visibility ${animationDuration}s linear`,
        visibility: show ? "visible" : "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        ...(!show && {
          animation: `${fadeOutAnimation} ${animationDuration}s ease`,
        }),
        left: 0,
        top: 0,
      }}
    >
      <CircularProgress
        variant="determinate"
        value={progress}
        size={loadingSize}
      />
      {children}
    </Box>
  );
};

export default Loading;
